import {IColumnText, ILanguageTextService, ILanguageTextServiceContext, IMessageText} from "../PA.Core.IF.Language";
import {IMessageProvider, IProgressProvider} from "../PA.Core.IF.Basic";
import {MessageProvider} from "../Basic/Messages";
import {ProgressProvider} from "../Basic/Progress";
import {PAdbContext} from "../PAdbContext";
import * as _ from "lodash";
import {LanguageTextServiceContext} from "./LanguageTextServiceContext";
import {IContinueConditionState, WaitForGroup} from "../Basic/WaitForGroup";
import {ILease} from "../../PA.Utils/PA.UtilsIF";
import {ExecutionSerializer} from "../Basic/ExecutionSerializer";
import {WB_DataObjectRow, WB_DomainRow} from "../PAdbSchemaRows";
import {GenericLease} from "../../PA.Utils/GenericLease";
import {LeaseRegistry} from "../../PA.Utils/LeaseRegistry";
import {ESRecordResolver} from "../lib/ESRecordResolver";
import {
    esWB_DataObject,
    esWB_DataObject_Column,
    esWB_DataObject_Column_Text,
    esWB_DataObject_Message,
    esWB_DataObject_Message_Text
} from "../PAdbSchema";
import {BehaviorSubject} from "rxjs";

export interface IGetOptions {
    languageCdSecondary?: string[]
}

export interface IDataObjectCache {
    [dataObjectTypeId:string]: {[dataObjectId: string]: IDataObjectCacheEntry}
}

export enum IDataObjectCacheEntryStatus {
    Init="Init",
    Queued = "Queued",
    Loaded="Loaded"
}

export interface IDataObjectCacheEntry {
    dataObjectTypeId: string,
    dataObjectId: string,

    dataObjectRow: WB_DataObjectRow,
    domainRow: WB_DomainRow,

    status: IDataObjectCacheEntryStatus
    lease: ILease

    queueLanguages: {[languageCd: string]: string}
    loadedLanguages: {[languageCd: string]: string}

    dctColumns: {[columnId: string]: IDataObjectColumnEntry}
    dctMessages: {[messageId: string]: IDataObjectMessageEntry}
}

export interface IDataObjectColumnEntry {
    columnId: string,
    columnName: string

    dctLanguageText: {[languageCd:string]: IColumnText}
}

export interface IDataObjectMessageEntry {
    messageId: string,

    dctLanguageMessage: {[languageCd:string]: IMessageText}
}

export interface ILanguageTextDataChangedEvent {
    Languages: string[],
    DataObjects: {Type: string, Id: string}[],
    DomainIds: string[]
}


export class LanguageTextService
    implements  ILanguageTextService
{
    //--private static Lock = new AsyncLock({})
    private static dctCache: IDataObjectCache = {}
    private static waitForDataGroup: WaitForGroup<any> = new WaitForGroup<any>(LanguageTextService)
    private static qsProcessLoadQueue: ExecutionSerializer= new ExecutionSerializer({}, LanguageTextService._LanguageText_processQueue, [],500)
    static onDataChanged: BehaviorSubject<ILanguageTextDataChangedEvent> = new BehaviorSubject<ILanguageTextDataChangedEvent>(undefined)
    private static dbContext: PAdbContext = undefined
    private static getDbContext(): PAdbContext {
        if(_.isEmpty(LanguageTextService.dbContext))
            LanguageTextService.dbContext = new PAdbContext({bDisableTracking: true})
        return LanguageTextService.dbContext
    }


    // Instance Variables
    private _loadMessageProvider: MessageProvider
    private _loadProgressProvider: ProgressProvider


    constructor() {
        this._loadMessageProvider = new MessageProvider()
        this._loadProgressProvider = new ProgressProvider("LanguageTextService")
    }

    get LoadMessagesProvider(): IMessageProvider { return this._loadMessageProvider }

    get LoadProgressProvider(): IProgressProvider { return this._loadProgressProvider }

    createServiceContext(languageCd: string | string[]): ILanguageTextServiceContext {
        return new LanguageTextServiceContext(this, languageCd);
    }


    //<editor-fold desc="Get Operations">

    getColumnText(dataObjectTypeId: string,
                  dataObjectId: string,
                  columnId: string,
                  languageCd: string,
                  options: IGetOptions): IColumnText
    {

        let ae = LanguageTextService.CacheEntry_Get(dataObjectTypeId, dataObjectId, true)


        function lookupLanguageText(aLC: string){
            if(!(columnId in ae.dctColumns))
                return undefined

            let ce = ae.dctColumns[columnId]
            if(!ce)
                return undefined

            if(aLC in ce.dctLanguageText)
                return ce.dctLanguageText[aLC]

            return undefined
        }

        if(languageCd in ae.loadedLanguages){
            let rv = lookupLanguageText(languageCd)
            if(rv)
                return rv

        } else {

            if (this.queueLanguage(ae, languageCd))
                this.queueDataObjectLoad(dataObjectTypeId, dataObjectId, languageCd)
        }

        let idx = languageCd.indexOf("-")
        if (idx > 0) {
            let fallbackLT = lookupLanguageText(languageCd.substring(0, idx))
            return fallbackLT
        }

        return undefined;
    }

    getColumnTextAsync(dataObjectTypeId: string,
                             dataObjectId: string,
                             columnId: string,
                             languageCd: string,
                             options: IGetOptions): Promise<IColumnText>
    {
        const self = this

        let ct = self.getColumnText(dataObjectTypeId, dataObjectId, columnId, languageCd, options)
        if (ct)
            return Promise.resolve(ct)

        let rv = LanguageTextService.waitForDataGroup.waitFor({}, (chkState: IContinueConditionState<any>) => {
            let bContinue = self.isDataObjectLoaded(dataObjectTypeId, dataObjectId, languageCd)
            return bContinue
        }).then((ignore) => {
            let cts = self.getColumnText(dataObjectTypeId, dataObjectId, columnId, languageCd, options)
            return cts
        })
        return rv

    }

    getMessage(dataObjectTypeId: string,
               dataObjectId: string,
               messageId: string,
               languageCd: string,
               options: IGetOptions): IMessageText
    {
        return undefined;
    }

    getMessageAsync(dataObjectTypeId: string,
                    dataObjectId: string,
                    messageId: string,
                    languageCd: string,
                    options: IGetOptions): Promise<IMessageText>
    {
        return Promise.resolve(undefined);
    }

    //</editor-fold>

    //<editor-fold desc="Load Operations">

    queueDataObjectLoad(dataObjectTypeId: string, dataObjectId: string, languageCd: string): void
    {
        let ae = LanguageTextService.CacheEntry_Get(dataObjectTypeId, dataObjectId, true)
        if(ae.status == IDataObjectCacheEntryStatus.Init){
            this.queueLanguage(ae, languageCd)
            ae.status = IDataObjectCacheEntryStatus.Queued
            LanguageTextService.LanguageText_processQueue()
        } else {
            if (this.queueLanguage(ae, languageCd))
                if (ae.status != IDataObjectCacheEntryStatus.Queued) {
                    ae.status = IDataObjectCacheEntryStatus.Queued
                    LanguageTextService.LanguageText_processQueue()
                }
        }
    }

    // returns if new languages is queued
    queueLanguage(ce: IDataObjectCacheEntry, languageCd: string): boolean {
        let bQueuedNewItem = false
        let arrLanguageCds = [languageCd]
        let idx = languageCd.indexOf("-")
        if(idx > 0)
            arrLanguageCds.push(languageCd.substring(0, idx))

        _.forEach(arrLanguageCds, (aCd) => {
            if(!(aCd in ce.queueLanguages)){
                ce.queueLanguages[aCd] = aCd
                bQueuedNewItem = true
            }
        })
        return bQueuedNewItem
    }

    isDataObjectLoaded(dataObjectTypeId: string, dataObjectId: string, languageCd: string): boolean
    {
        let ae = LanguageTextService.CacheEntry_Get(dataObjectTypeId, dataObjectId, false)

        if(_.isEmpty(ae)){
            this.queueDataObjectLoad(dataObjectTypeId, dataObjectId, languageCd)
            return false
        }

        return languageCd in ae.loadedLanguages
    }

    //</editor-fold>

    //<editor-fold desc="Static Load Operations">


    private static CacheEntry_Get(dataObjectTypeId: string, dataObjectId: string, bCreateIfMissing?: boolean): IDataObjectCacheEntry
    {
        //-- Find Type
        if(!(dataObjectTypeId in LanguageTextService.dctCache)){
            if(!bCreateIfMissing)
                return undefined

            LanguageTextService.dctCache[dataObjectTypeId] = {}
        }

        if(!(dataObjectId in LanguageTextService.dctCache[dataObjectTypeId])){
            if(!bCreateIfMissing)
                return undefined

            let doLease = new GenericLease(LeaseRegistry.Lease_Type_DataObject)
            doLease.addClass(`${LeaseRegistry.Lease_Type_DataObject}_${dataObjectTypeId}_${dataObjectId}`)

            let rv: IDataObjectCacheEntry = {
                dataObjectTypeId: dataObjectTypeId,
                dataObjectId: dataObjectId,
                dataObjectRow: undefined,
                domainRow: undefined,
                lease: doLease,
                status: IDataObjectCacheEntryStatus.Init,
                queueLanguages: {},
                loadedLanguages: {},
                dctColumns: {},
                dctMessages: {}
            }

            LanguageTextService.dctCache[dataObjectTypeId][dataObjectId] = rv


        }

        return LanguageTextService.dctCache[dataObjectTypeId][dataObjectId]

    }


    private static LanguageText_processQueue(): void {
        LanguageTextService.qsProcessLoadQueue.Run({})
    }

    private static async _LanguageText_processQueue(): Promise<void> {

        let self = this
        let bDebugQueueProcessing = true

        function logDebug(...args: any[]) {
            if (bDebugQueueProcessing)
                console.log(...args)
        }

        let changeEvent: ILanguageTextDataChangedEvent = {
            Languages: [],
            DataObjects: [],
            DomainIds: []
        }

        let queueLanguages: {[languageCd: string]: string} = {}
        let queueDataObjectTypes: {[doTypeId: string]: string} = {}
        let queueDataObjectIds: {[doId: string]: string} = {}
        let queueDomainIds: {[domainId: string]: string} = {}

        //--
        // Determine what needs to be loaded
        _.forEach(LanguageTextService.dctCache, (dctDO, dataObjectTypeId) => {
            _.forEach(dctDO, (ce, dataObjectId) => {
                //--
                if (ce.status != IDataObjectCacheEntryStatus.Queued)
                    return

                //-- Add unloaded Languages
                _.forEach(ce.queueLanguages, (v, k) => {
                    if (!(k in ce.loadedLanguages))
                        queueLanguages[k] = v
                })

                if(ce.dataObjectTypeId == "Domain"){
                    if (!(ce.dataObjectId in queueDomainIds))
                        queueDomainIds[ce.dataObjectId] = ce.dataObjectId
                    changeEvent.DomainIds.push(ce.dataObjectId)

                } else {
                    changeEvent.DataObjects.push({Type: ce.dataObjectTypeId, Id: ce.dataObjectId })
                    //-- DataObject Type
                    if (!(ce.dataObjectTypeId in queueDataObjectTypes))
                        queueDataObjectTypes[ce.dataObjectTypeId] = ce.dataObjectTypeId

                    if (!(ce.dataObjectId in queueDataObjectIds))
                        queueDataObjectIds[ce.dataObjectId] = ce.dataObjectId
                }
            }) // Next DO Id
        }) // Next DO Type

        let bLoadDataObjects = !(_.isEmpty(queueDataObjectIds) || _.isEmpty(queueLanguages))
        let bLoadDomains = !(_.isEmpty(queueDomainIds) || _.isEmpty(queueLanguages))

        if((!bLoadDataObjects) && (!bLoadDomains)) {
            // Nothing to load
            logDebug("LanguageTextService: load queue empty" )
            return
        }
        changeEvent.Languages = _.values(queueLanguages)

        // Use Record resolver to Load
        if(bLoadDataObjects){

            let rrDO = new ESRecordResolver(LanguageTextService.getDbContext(), {
                standardConditions: {
                    [esWB_DataObject]:              {DELETED_IND: {notIn: ['1']}},
                    [esWB_DataObject_Column]:       {DELETED_IND: {notIn: ['1']}},
                    [esWB_DataObject_Column_Text]:  {DELETED_IND: {notIn: ['1']}, USAGE: "Name", LANGUAGE: {inq: _.values(queueLanguages)}},
                    [esWB_DataObject_Message]:      {DELETED_IND: {notIn: ['1']}},
                    [esWB_DataObject_Message_Text]: {DELETED_IND: {notIn: ['1']}, LANGUAGE: {inq: _.values(queueLanguages)}},
                }
            })

            let doCnd = {where: {DATAOBJECT_ID: {inq: _.values(queueDataObjectIds)},
                                 DATAOBJECT_TYPE: {inq: _.values(queueDataObjectTypes)},
                                 DELETED_IND: {notIn: ['1']}}}

            let arrDOs: WB_DataObjectRow[] = <WB_DataObjectRow[]> await rrDO.queryAllWithRelationships(esWB_DataObject, doCnd)

            // Update the Cache Entries, mark the languages loaded, set status to loaded if all languages loaded.
            _.forEach(changeEvent.DataObjects, (aDO) => {

                let de = LanguageTextService.CacheEntry_Get(aDO.Type, aDO.Id)
                if(_.isEmpty(de))
                    return

                let aDORow: WB_DataObjectRow = _.first(_.filter(arrDOs, (a) => ((a.cif.DATAOBJECT_TYPE == aDO.Type) && (a.cif.DATAOBJECT_ID == aDO.Id)) ))
                if(!_.isEmpty(aDORow)) {

                    // Add Columns
                    _.forEach(aDORow.WB_DataObjectColumnRows, (aDOCRow) => {
                        let columnId = aDOCRow.cif.COLUMN_ID
                        let columnName = aDOCRow.cif.COLUMN_NAME

                        if (!(columnId in de.dctColumns)) {
                            de.dctColumns[columnId] = {
                                columnId: columnId,
                                columnName: columnName,
                                dctLanguageText: {}
                            }
                        }

                        let ce = de.dctColumns[columnId]

                        _.forEach(aDOCRow.WB_DataObjectColumnTextRows, (aCTRow) => {
                            let language = aCTRow.cif.LANGUAGE

                            ce.dctLanguageText[language] = {
                                LanguageCode: language,
                                FallbackName: columnName,
                                Text: aCTRow.cif.TEXT,
                                ShortText: aCTRow.cif.SHORTTEXT,
                                Abbreviation: aCTRow.cif.ABBREVIATION,
                                Tooltip: aCTRow.cif.TOOLTIP
                            }
                        }) // Next Language Text

                    }) // Next Column

                    //-- TODO:  Load Messages

                }

                //--
                // Mark Languages Loaded
                _.forEach(queueLanguages, (a) => {
                    de.loadedLanguages[a] = a
                })

                // Check if all Queued languages are loaded
                let bMarkLoaded = true
                _.forEach(de.queueLanguages, (a) => {
                    if(!(a in de.loadedLanguages ))
                        bMarkLoaded = false
                })

                //--
                if(bMarkLoaded){
                    de.status = IDataObjectCacheEntryStatus.Loaded
                }

            }) // Next Data Object

        } // bLoadDataObject

        LanguageTextService.waitForDataGroup.checkContinueConditions()
        LanguageTextService.onDataChanged.next(changeEvent)

    } // End Load Process

    //</editor-fold>


}

